import { useToast } from '@hooks/useToast';
import { isApiError } from '@services/auth.service';
import { reactivateProject } from '@services/projects.service';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

/**
 * Reactivating a project touches four independent caches. Every call site used
 * to pick its own subset, which is how a resume could clear one gate and leave
 * another rendering stale money: the blocker overlay invalidated only
 * `['wallet','status']`, so `['projects','list']` kept reporting `suspended` and
 * a second paywall screen appeared quoting a pre-debit balance.
 *
 * The affected keys, and who reads them:
 *   - `['wallet']`            — useWalletBalance, drives the topbar balance
 *   - `['wallet','status',p]` — useWalletStatus, drives the blocker overlay
 *   - `['projects','list']`   — useActiveProject, drives subscription status
 *   - `['projects', id]`      — project detail
 *   - `['paywall-info', id]`  — overdue + customer count
 *
 * Invalidation below is by PREFIX: React Query matches any key starting with the
 * given array, so `['wallet']` covers `['wallet','status',projectId]` and
 * `['projects']` covers both the list and the detail. Three prefixes cover all
 * five, and no call site can forget one.
 *
 * Reactivation is the one action that changes all of them at once (it debits,
 * flips status, and lifts the gate), so it owns invalidating them. Keep this the
 * ONLY place that calls `reactivateProject`.
 */
export interface UseReactivateProjectOptions {
  /** Runs after the caches are invalidated. Use to close a modal or overlay. */
  onSuccess?: () => void;
  /**
   * Return true to signal the error was handled and the default toast should be
   * suppressed — e.g. a 402 rendered as an InsufficientBalanceModal instead.
   */
  onError?: (err: unknown) => boolean | void;
  /** Set false when the caller renders its own success feedback. */
  showSuccessToast?: boolean;
}

export function useReactivateProject(
  projectId: string | null | undefined,
  options: UseReactivateProjectOptions = {},
): UseMutationResult<unknown, unknown, void> {
  const { onSuccess, onError, showSuccessToast = true } = options;
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async () => {
      if (!projectId) {
        // Guard rather than firing `/projects/undefined/reactivate`, which
        // 404s and surfaces as a confusing "could not reactivate".
        throw new Error('NO_PROJECT_SELECTED');
      }
      return reactivateProject(projectId);
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['wallet'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['paywall-info'] }),
      ]);
      if (showSuccessToast) {
        toast.success({
          title: 'Service resumed',
          message: 'Outbound messaging is active again.',
        });
      }
      onSuccess?.();
    },
    onError: (err) => {
      if (onError?.(err) === true) return;
      if (err instanceof Error && err.message === 'NO_PROJECT_SELECTED') {
        toast.error({
          title: 'No number selected',
          message: 'Pick a WhatsApp number first, then try again.',
        });
        return;
      }
      toast.error({
        title: 'Could not resume service',
        message: isApiError(err)
          ? err.message
          : 'Please try again, or contact support if this keeps happening.',
      });
    },
  });
}

export default useReactivateProject;
